// iter-34 — Users Tab.
//
// Shows the creator's audience aggregated into 4 groups across all their
// silos (or one specific silo when scope='silo'). Includes a referral-base
// bonus bracket meter that PREVIEWS the iter-38 group bonus pool — values
// are hardcoded and clearly labeled "preview only" until iter-38 ships the
// admin bracket UI.
//
// Used in two places:
//   1) Creator Dashboard → "Users" tab (scope='me') — aggregate across all my silos
//   2) SiloManage      → "Users" tab (scope='silo', siloId=<id>) — single silo

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  UserCircle, Users, UsersThree, Crown, Medal, ArrowRight, TrendUp, Lock,
  ChartBar, Sparkle, Eye, EnvelopeSimple, CurrencyDollar,
  MagnifyingGlass, X, CaretLeft, CaretRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { usePublicPricing, fmtPct } from "@/lib/usePublicPricing";

const fmt$ = (cents) => `$${((cents || 0) / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
const relTime = (iso) => {
  if (!iso) return "—";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60_000) return "just now";
    if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
    if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
    return `${Math.floor(ms / 86_400_000)}d ago`;
  } catch { return "—"; }
};

// Feb 20, 2026 — Friendly creator status pill. Translates the raw referral
// state machine ("awaiting_settle" / "awarded" / etc.) into copy a human
// can read. "awaiting_settle" specifically means the 30-day hold before
// the referral becomes permanent attribution — NOT a payment status.
function CreatorStatusPill({ status }) {
  const map = {
    awarded:         { label: "Settled",    bg: "#E8F0E5", fg: "#2F6B3A" },
    awaiting_settle: { label: "Settling",   bg: "#F8F4E8", fg: "#7A5A1A" },
    awaiting_signup: { label: "Pending",    bg: "#F0F1EE", fg: "#6B6A68" },
    parked:          { label: "Parked",     bg: "#F0F1EE", fg: "#6B6A68" },
    pending:         { label: "Pending",    bg: "#F0F1EE", fg: "#6B6A68" },
  };
  const cfg = map[status] || { label: status || "—", bg: "#F0F1EE", fg: "#4A5D4E" };
  const title = status === "awaiting_settle"
    ? "30-day hold before the referral becomes permanent. Doesn't affect their account or your rev-share — pure attribution housekeeping."
    : undefined;
  return (
    <span
      className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full"
      style={{ background: cfg.bg, color: cfg.fg }}
      title={title}
      data-testid={`creator-status-${status || "unknown"}`}
    >
      {cfg.label}
    </span>
  );
}

export default function UsersTab({ scope = "me", siloId = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeGroup, setActiveGroup] = useState("paying");
  const [isAffiliate, setIsAffiliate] = useState(false);
  const pricing = usePublicPricing();
  const basePct = fmtPct(pricing.affiliate_base_rev_share_pct);
  const perfPct = fmtPct(pricing.affiliate_performance_rev_share_pct);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const url = scope === "silo" && siloId
          ? `/silos/${siloId}/users-tab`
          : "/me/users-tab";
        const [r, meR] = await Promise.all([
          api.get(url),
          api.get("/auth/me").catch(() => null),
        ]);
        if (!cancel) {
          setData(r.data);
          // Jun 2026 (Randy) — the founder/super_admin is ABOVE affiliate, so
          // they should never see the "upgrade to affiliate" lock on the
          // Creators / Affiliate Creators pills. Treat super_admin as unlocked.
          setIsAffiliate(
            !!meR?.data?.is_affiliate ||
            meR?.data?.role === "super_admin" ||
            meR?.data?.derived_role === "super_admin"
          );
        }
      } catch (e) {
        if (!cancel) toast.error(e.response?.data?.detail || "Failed to load Users tab.");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [scope, siloId]);

  if (loading) return <div className="text-[#6B6A68] text-sm" data-testid="users-tab-loading">Loading audience…</div>;
  if (!data) return null;

  const g = data.groups;
  const dl = data.referral_base;
  const interactionsCount = g.interactions?.count || 0;
  const totalAudience = g.anonymous.count + g.non_paying.count + interactionsCount + g.paying.count + g.creators.count + (g.affiliate_creators?.count || 0);
  const bracketProgress = dl.next_bracket
    ? Math.min(100, Math.round((dl.awarded_count / dl.next_bracket.min_count) * 100))
    : 100;

  const groupConfigs = [
    {
      key: "anonymous", label: "Anonymous", sublabel: "Visitors", Icon: Eye, color: "#6B6A68",
      desc: "Visitors who've asked questions but never signed up.",
      group: g.anonymous,
    },
    {
      // Oct 2026 (Randy) — was "Fans", which read as the umbrella over Free
      // followers + Paying. It is not: subscribers are EXCLUDED here. Renamed
      // to what it counts. The old sublabel was wrong twice over — there is no
      // "comment" interaction kind (only `star` and `chat`), and asking a
      // question while signed in was missing.
      key: "interactions", label: "Engaged", sublabel: "Asked · starred", Icon: Sparkle, color: "#A89360",
      desc: "Signed-in people who asked this silo a question or starred it — or were demoted from a paid sub — but aren't currently subscribed. Your warmest non-paying audience.",
      group: g.interactions || { count: 0, recent: [] },
    },
    {
      // Feb 21, 2026 — clarified "Fans (free subs)" → "Free followers" so
      // it doesn't fight with the new Fans bucket above. Same meaning:
      // logged-in users who tapped Subscribe on a $0 silo (free-follow).
      key: "non_paying", label: "Free followers", sublabel: "Followed at $0", Icon: UserCircle, color: "#C16E5A",
      desc: "Signed-in fans who followed a free silo. If you raise the price, they get demoted to the Engaged bucket above.",
      group: g.non_paying,
    },
    {
      key: "paying", label: "Paying", sublabel: "Active fans", Icon: Users, color: "#4A5D4E",
      desc: "Active fans paying for silo access. Your most engaged audience.",
      group: g.paying,
    },
    {
      key: "creators", label: "Creators", sublabel: "From your silo", Icon: Crown, color: "#1C1B1A",
      desc: `Users who upgraded to Creator after going through your silo. If you're in the Partner Program (Creator Pro), you earn ${basePct} of their My Silo subscription (${perfPct} at the Performance tier) for as long as they keep paying.`,
      group: g.creators,
    },
    {
      // Jun 2026 — clean split from Creators: your downline creators who've
      // paid the affiliate gate (is_affiliate). Separate tile so totals don't
      // double-count and the vocabulary matches the "Asked by" tags.
      key: "affiliate_creators", label: "Creator Pro", sublabel: "You referred", Icon: Medal, color: "#6B4FA0",
      desc: "Creators you referred who've upgraded to Creator Pro.",
      group: g.affiliate_creators || { count: 0, recent: [] },
    },
  ];

  return (
    <div data-testid={`users-tab-${scope}`}>
      {/* Header */}
      <div className="mb-6">
        <h2 className="font-serif-display text-2xl text-[#1C1B1A]">
          {scope === "silo" ? "This silo's audience." : "Your audience."}
        </h2>
        <p className="text-sm text-[#6B6A68] mt-1 max-w-2xl">
          {scope === "silo"
            ? "Everyone interacting with this specific silo, grouped by tier."
            : `Aggregated across all ${data.silo_count} of your silo${data.silo_count === 1 ? "" : "s"}. Click a group to browse and search its members.`}
        </p>
      </div>

      {/* 4-up group cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="users-tab-summary-grid">
        {groupConfigs
          .filter((gc) => isAffiliate || (gc.key !== "creators" && gc.key !== "affiliate_creators"))
          .map(({ key, label, sublabel, Icon, color, group }) => {
          // Feb 19, 2026 — role segregation: non-affiliates see the "Creators"
          // group greyed out with an upsell tooltip, since they don't earn
          // rev share until they upgrade to Affiliate Creator.
          const isLocked = (key === "creators" || key === "affiliate_creators") && !isAffiliate;
          const tooltip = isLocked
            ? `Upgrade to Creator Pro to unlock the full My Silo toolkit.`
            : undefined;
          return (
            <button
              key={key}
              onClick={() => { if (!isLocked) setActiveGroup(key); }}
              disabled={isLocked}
              title={tooltip}
              data-testid={`users-tab-group-${key}${isLocked ? "-locked" : ""}`}
              className={`relative text-left bg-white border rounded-xl p-5 transition-all ${
                isLocked
                  ? "opacity-50 cursor-not-allowed hairline"
                  : activeGroup === key
                    ? "border-[#4A5D4E] shadow-md ring-1 ring-[#4A5D4E]/20"
                    : "hairline hover:border-[#4A5D4E]/40"
              }`}
            >
              {isLocked && (
                <span className="absolute top-3 right-3 text-[#6B6A68]" data-testid="users-tab-creators-lock">
                  <Lock size={14} weight="duotone" />
                </span>
              )}
              <Icon size={18} weight="duotone" style={{ color }} />
              {/* iter-35f: two-line label — primary noun + parenthetical
                  descriptor underneath, applied to all 4 cards for visual
                  consistency. */}
              <div className="mt-3 leading-tight">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#1C1B1A] font-medium">{label}</div>
                <div className="text-[9px] uppercase tracking-[0.22em] text-[#6B6A68] mt-0.5">({sublabel})</div>
              </div>
              <div className="font-serif-display text-3xl tabular-nums text-[#1C1B1A] mt-1.5" data-testid={`users-tab-count-${key}`}>
                {group.count.toLocaleString()}
              </div>
              <div className="text-[10px] text-[#6B6A68] mt-1">
                {isLocked
                  ? "Creator Pro only"
                  : totalAudience > 0 ? `${Math.round((group.count / totalAudience) * 100)}% of audience` : "—"}
              </div>
            </button>
          );
        })}
      </div>

      {/* iter-35f: zero-audience hint — when this silo (or all of the
          creator's silos in 'me' scope) has zero audience members across
          every tier, surface a friendly explainer rather than leaving the
          summary cards looking like a broken aggregator. */}
      {totalAudience === 0 && (
        <div className="bg-[#FDFCF8] border-2 border-dashed hairline rounded-xl p-6 text-center mb-8" data-testid="users-tab-zero-audience-hint">
          <p className="font-serif-display text-base text-[#1C1B1A]">No audience yet — that's expected.</p>
          <p className="text-sm text-[#6B6A68] mt-1.5 max-w-md mx-auto">
            {scope === "silo"
              ? "Share this silo's public link so anonymous visitors can find it. Counts populate as soon as someone asks a question or subscribes."
              : "Share your silos' public links so anonymous visitors can find them. Counts populate the moment someone asks a question or subscribes."}
          </p>
        </div>
      )}

      {/* Referral-base bonus meter — preview iter-38 */}
      {/* Feb 18, 2026 — Removed "Referral-base bonus tier" banner.
          Referenced a $X/signup + Y% recurring bracket system that
          contradicted the actual 25%/30% rev-share + $75 inbound bounty
          engine. Pure UI artifact from an abandoned design. Real
          earnings live in the Earnings tab. */}

      {/* Recent list for the active group */}
      <RecentList config={groupConfigs.find((c) => c.key === activeGroup)} scope={scope} siloId={siloId} />
    </div>
  );
}

function RecentList({ config, scope = "me", siloId = null }) {
  const navigate = useNavigate();
  const PAGE_SIZE = 25;
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const key = config?.key;

  const fetchPage = async (p, query) => {
    if (!key) return;
    setLoading(true);
    try {
      const base = scope === "silo" && siloId
        ? `/silos/${siloId}/users-tab/members`
        : "/me/users-tab/members";
      const r = await api.get(`${base}?group=${key}&page=${p}&size=${PAGE_SIZE}&q=${encodeURIComponent(query)}`);
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
      setPage(r.data.page || 1);
      setPages(r.data.pages || 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load members.");
    } finally { setLoading(false); }
  };

  // Clear the search + reset paging whenever the active group changes.
  useEffect(() => { setQ(""); setPage(1); }, [key]);
  // Load page 1 on group-change and (debounced) on search. Fires immediately
  // for an empty query, 300ms while typing.
  useEffect(() => {
    if (!key) return;
    const t = setTimeout(() => { fetchPage(1, q); }, q ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, q, scope, siloId]);

  if (!config) return null;
  const { label, group, color } = config;
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  return (
    <div className="bg-white border hairline rounded-xl p-6" data-testid={`users-tab-recent-${key}`}>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h3 className="font-serif-display text-lg text-[#1C1B1A]">All {label.toLowerCase()}</h3>
        <span className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]" data-testid={`users-tab-showing-${key}`}>
          {total === 0 ? "—" : `showing ${from.toLocaleString()}–${to.toLocaleString()} of ${total.toLocaleString()}`}
        </span>
      </div>
      <p className="text-xs text-[#6B6A68] mb-4">{config.desc}</p>

      {/* Search this group (server-side, by name / email / silo) */}
      <div className="relative mb-4 w-full sm:w-80">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A09E9B]" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={key === "anonymous" ? "Search by visitor tag or silo…" : "Search by name, email, or silo…"}
          data-testid={`users-tab-search-${key}`}
          className="w-full pl-9 pr-9 py-2 text-sm bg-white border hairline rounded-md text-[#1C1B1A] placeholder-[#A09E9B] focus:outline-none focus:border-[#4A5D4E]"
        />
        {q ? (
          <button type="button" onClick={() => setQ("")} data-testid={`users-tab-search-clear-${key}`}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-[#A09E9B] hover:text-[#C16E5A]">
            <X size={13} />
          </button>
        ) : null}
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed hairline rounded-md" data-testid={`users-tab-empty-${key}`}>
          <ChartBar size={28} weight="duotone" className="mx-auto" style={{ color }} />
          <p className="font-serif-display text-base mt-2 text-[#1C1B1A]">
            {loading ? "Loading…" : q ? `No ${label.toLowerCase()} match "${q}".` : `No ${label.toLowerCase()} yet.`}
          </p>
          {!q && !loading && (
            <p className="text-xs text-[#6B6A68] mt-1">
              {key === "anonymous" && "Share your silo's public link to attract first-time visitors."}
              {key === "non_paying" && "When a fan signs up via your silo without paying, they'll appear here."}
              {key === "paying" && "Set a price on your silo to start earning recurring fan revenue."}
              {key === "creators" && "Share your personal invite link to widen your referral base."}
            </p>
          )}
        </div>
      ) : (
        <ul className="divide-y hairline">
          {rows.map((row, i) => (
            <li key={`${key}-${i}`} className="py-3 flex items-center gap-4 text-sm" data-testid={`users-tab-recent-row-${key}-${i}`}>
              {key === "anonymous" ? (
                <>
                  <span className="text-xs bg-[#F0F1EE] px-2 py-1 rounded text-[#6B6A68]" data-testid={`users-tab-anon-sig-${i}`}>
                    Visitor #{from + i}
                    <span className="font-mono text-[10px] text-[#A8A6A2] ml-1.5">· {row.visitor_sig_short || "—"}</span>
                  </span>
                  <span className="text-[#6B6A68] text-xs">{row.session_count} session{row.session_count === 1 ? "" : "s"} on <strong className="text-[#1C1B1A]">{row.silo_name}</strong></span>
                  <span className="ml-auto text-xs text-[#6B6A68]">{relTime(row.last_active)}</span>
                </>
              ) : (key === "creators" || key === "affiliate_creators") ? (
                <>
                  {key === "affiliate_creators"
                    ? <Medal size={14} weight="duotone" className="text-[#6B4FA0]" />
                    : <Crown size={14} weight="duotone" className="text-[#C16E5A]" />}
                  <span className="font-medium text-[#1C1B1A]">{row.name || row.email}</span>
                  <CreatorStatusPill status={row.status} />
                  <span className="text-xs text-[#4A5D4E] tabular-nums ml-auto">{fmt$(row.credit_cents)} credit</span>
                  <span className="text-xs text-[#6B6A68]">{relTime(row.joined_at)}</span>
                </>
              ) : key === "non_paying" ? (
                <>
                  <UserCircle size={14} weight="duotone" className="text-[#C16E5A]" />
                  <span className="font-medium text-[#1C1B1A] truncate">{row.name || row.email.split("@")[0]}</span>
                  <span className="text-xs text-[#6B6A68]">on <strong className="text-[#1C1B1A]">{row.silo_name}</strong></span>
                  {row.source === "auto_follow_signup" && (
                    <span className="text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded bg-[#FDF2EE] text-[#C16E5A]" data-testid={`users-tab-source-auto-${i}`}>auto-follow</span>
                  )}
                  <span className="text-xs text-[#6B6A68] ml-auto">{relTime(row.subscribed_at)}</span>
                </>
              ) : key === "interactions" ? (
                <>
                  <Sparkle size={14} weight="duotone" className="text-[#A89360]" />
                  <span className="font-medium text-[#1C1B1A] truncate">{row.name || row.email.split("@")[0]}</span>
                  <span className="text-xs text-[#6B6A68]">on <strong className="text-[#1C1B1A]">{row.silo_name}</strong></span>
                  <span className="text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded bg-[#F8F4E8] text-[#A89360]">
                    {(row.kinds || []).join(" + ")}
                  </span>
                  <span className="text-xs text-[#6B6A68] ml-auto">{relTime(row.last_at)}</span>
                </>
              ) : (
                <>
                  <Users size={14} weight="duotone" className="text-[#4A5D4E]" />
                  <span className="font-medium text-[#1C1B1A] truncate">{row.name || row.email.split("@")[0]}</span>
                  <span className="text-xs text-[#6B6A68]">on <strong className="text-[#1C1B1A]">{row.silo_name}</strong></span>
                  {row.is_free_tier && (
                    <span className="text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded bg-[#F0F1EE] text-[#4A5D4E]" data-testid={`users-tab-tier-free-${i}`}>free-tier</span>
                  )}
                  {!row.is_free_tier && row.price_cents > 0 && (
                    <span className="text-[10px] text-[#4A5D4E] tabular-nums" data-testid={`users-tab-price-${i}`}>{fmt$(row.price_cents)}/mo</span>
                  )}
                  <span className="text-xs text-[#6B6A68] ml-auto">{relTime(row.subscribed_at)}</span>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Prev/Next pager */}
      {total > 0 && (
        <div className="flex items-center justify-between gap-3 mt-4 text-sm text-[#6B6A68]" data-testid={`users-tab-pager-${key}`}>
          <span>{total.toLocaleString()} total</span>
          <div className="flex items-center gap-2">
            <button type="button" disabled={page <= 1 || loading} onClick={() => fetchPage(page - 1, q)} data-testid={`users-tab-prev-${key}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border hairline bg-white text-[#1C1B1A] hover:bg-[#F0F1EE] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors">
              <CaretLeft size={12} weight="bold" /> Prev
            </button>
            <span className="tabular-nums text-xs" data-testid={`users-tab-pageinfo-${key}`}>Page {page} / {pages}</span>
            <button type="button" disabled={page >= pages || loading} onClick={() => fetchPage(page + 1, q)} data-testid={`users-tab-next-${key}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border hairline bg-white text-[#1C1B1A] hover:bg-[#F0F1EE] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors">
              Next <CaretRight size={12} weight="bold" />
            </button>
          </div>
        </div>
      )}

      {/* Quick-action footer — emails the WHOLE group (summary count, not the
          filtered page). */}
      {group.count > 0 && key !== "anonymous" && (
        <div className="mt-5 pt-4 border-t hairline flex items-center gap-3 text-xs text-[#6B6A68] flex-wrap">
          <button
            onClick={() => {
              sessionStorage.setItem("comms_audience_filter", key);
              sessionStorage.setItem("dashboard_tab", "comms");
              const onDashboard = window.location.pathname === "/dashboard";
              if (onDashboard) {
                window.dispatchEvent(new CustomEvent("dashboard-scroll-to", { detail: { tab: "comms" } }));
                window.dispatchEvent(new CustomEvent("comms-set-audience", { detail: { audience: key } }));
              } else {
                navigate("/dashboard");
              }
            }}
            data-testid={`users-tab-email-group-${key}`}
            className="text-xs px-3 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5"
          >
            <EnvelopeSimple size={12} /> Email this group ({group.count})
          </button>
          {key === "non_paying" && <span>Pre-fills the Communications composer with everyone who follows you at $0.</span>}
          {key === "interactions" && <span>People who touched your silo but haven't subscribed — warmest leads.</span>}
          {key === "paying" && <span>{group.count} active subscriber{group.count === 1 ? "" : "s"} = your recurring revenue base.</span>}
          {key === "creators" && <span>Talk directly to the Creator Pro members you've referred.</span>}
        </div>
      )}

      {/* Anonymous bucket — historical monthly aggregates. */}
      {key === "anonymous" && <AnonHistoryPanel />}
    </div>
  );
}

function AnonHistoryPanel() {
  const [open, setOpen] = useState(false);
  const [months, setMonths] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/me/users-tab/anon-history?months=24");
      setMonths(r.data?.months || []);
    } catch { setMonths([]); }
    finally { setLoading(false); }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && months === null) load();
  };

  return (
    <div className="mt-5 pt-4 border-t hairline" data-testid="anon-history-panel">
      <button
        onClick={toggle}
        className="text-xs text-[#4A5D4E] hover:underline flex items-center gap-1.5"
        data-testid="anon-history-toggle"
      >
        <ChartBar size={12} weight="duotone" />
        {open ? "Hide historical results" : "Show historical results"}
      </button>
      {open && (
        <div className="mt-4">
          {loading && <p className="text-xs text-[#6B6A68]">Loading…</p>}
          {!loading && months && months.length === 0 && (
            <p className="text-xs text-[#6B6A68]">No historical data yet. Aggregates start counting from today forward.</p>
          )}
          {!loading && months && months.length > 0 && (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-[#6B6A68]">
                  <th className="py-1.5">Month</th>
                  <th className="py-1.5 text-right">Anonymous questions</th>
                </tr>
              </thead>
              <tbody className="divide-y hairline">
                {months.map((m) => (
                  <tr key={m.year_month}>
                    <td className="py-1.5 text-[#1C1B1A]">{m.year_month}</td>
                    <td className="py-1.5 text-right tabular-nums text-[#1C1B1A]">{m.count.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
